import ApiResourceManager from '../ApiResourceManager';

/**
 * The partner's own connected gateways. Always acts on the signed-in partner
 * admin's provider — the tenant middleware pins non-privileged users to it — so
 * unlike the SuperAdmin managers there is no on-behalf override.
 *
 * The list response carries the Stripe restricted-key permission list in its
 * meta, so the "How to create this key" expander renders what the backend
 * probes actually require instead of a second copy that can drift.
 */
export default class PartnerGatewaysManager extends ApiResourceManager {
  list = () =>
    this.request({
      method: 'GET',
      url: '/partner-settings/payments/gateways',
    });

  /**
   * Both keys come off the same Stripe API keys page. Posting again on a
   * partner that is already connected rotates the key in place rather than
   * adding a second connection.
   */
  connectStripe = (data) =>
    this.request({
      method: 'POST',
      url: '/partner-settings/payments/gateways/stripe-connect',
      data,
    });

  /**
   * The gateways this partner may connect itself, region- and custody-filtered
   * server-side, each with the credential fields its form renders.
   */
  catalogue = () =>
    this.request({
      method: 'GET',
      url: '/partner-settings/payments/gateways/catalogue',
    });

  /**
   * `{ gateway_type, auth_mode_type?, credentials, name? }`. Answers once the
   * acquirer has accepted the credentials, or 422 naming why not.
   */
  connectOrchestrated = (data) =>
    this.request({
      method: 'POST',
      url: '/partner-settings/payments/gateways/orchestrated',
      data,
    });

  /** Same connection, same restaurants — only the account behind it changes. */
  replaceCredentials = (connectionId, data) =>
    this.request({
      method: 'POST',
      url: `/partner-settings/payments/gateways/${connectionId}/credentials`,
      data,
    });

  /**
   * "Check again" on a connection Stripe refused: provisions every restaurant
   * still waiting on it and answers the connection as it stands afterwards —
   * `issue` gone, or still there with what Stripe said this time.
   */
  retryProvisioning = (connectionId) =>
    this.request({
      method: 'POST',
      url: `/partner-settings/payments/gateways/${connectionId}/retry-provisioning`,
    });

  rename = (connectionId, name) =>
    this.request({
      method: 'PUT',
      url: `/partner-settings/payments/gateways/${connectionId}`,
      data: { name },
    });

  /**
   * `{ enabled_payment_methods, stripe_publishable_key? }` — the local methods (iDEAL, …) a
   * connection takes. 422 names a method the gateway cannot take, a key in the wrong mode, or a
   * method already on another card-less connection.
   */
  updatePaymentMethods = (connectionId, data) =>
    this.request({
      method: 'PUT',
      url: `/partner-settings/payments/gateways/${connectionId}/payment-methods`,
      data,
    });

  setPrimary = (connectionId) =>
    this.request({
      method: 'PUT',
      url: `/partner-settings/payments/gateways/${connectionId}/primary`,
    });

  /** New restaurants start unlinked afterwards; the ones already linked keep their gateway. */
  clearPrimary = (connectionId) =>
    this.request({
      method: 'DELETE',
      url: `/partner-settings/payments/gateways/${connectionId}/primary`,
    });

  /**
   * Refused with `partner_gateway_still_live` while a restaurant charges through it, unless
   * `pauseLiveRestaurants` confirms that those restaurants stop taking online payments.
   */
  disconnect = (connectionId, { pauseLiveRestaurants = false } = {}) =>
    this.request({
      method: 'DELETE',
      url: `/partner-settings/payments/gateways/${connectionId}`,
      data: pauseLiveRestaurants ? { pause_live_restaurants: true } : undefined,
    });

  /** Only a disconnected connection the response marks `is_removable`. */
  remove = (connectionId) =>
    this.request({
      method: 'POST',
      url: `/partner-settings/payments/gateways/${connectionId}/remove`,
    });

  /**
   * Every restaurant of the partner with its payment-setup status. `meta.counts`
   * holds `all` / `needs_attention` regardless of the filter, for the chips.
   */
  listRestaurants = (params = {}) =>
    this.request({
      method: 'GET',
      url: '/partner-settings/payments/restaurants',
      params,
    });

  /** 429 with `payload.available_at` while the one-a-day cooldown runs. */
  remindOwner = (companyId) =>
    this.request({
      method: 'POST',
      url: `/partner-settings/payments/restaurants/${companyId}/remind`,
    });

  /**
   * Drops the restaurant's Stripe Connect account so its next "Continue" onboards a new
   * one. A live restaurant needs `pauseOnlinePayments`; refusals come back as 409 (502
   * when Stripe could not be asked) with the cause in `error_code`; a provisioning still
   * running for the restaurant answers `provisioning_in_progress`, worth a retry.
   */
  startStripeSetupOver = (companyId, { pauseOnlinePayments = false } = {}) =>
    this.request({
      method: 'POST',
      url: `/partner-settings/payments/restaurants/${companyId}/start-stripe-setup-over`,
      data: pauseOnlinePayments ? { pause_online_payments: true } : {},
    });
}
